
import Vue from "vue";
import type { Vendor } from "@/services/OAuthService";

/**
 * Interface describing a sync message received via SSE.
 */
interface SyncMessage {
  type: string; // e.g. 'start', 'progress', 'info', 'warn', 'error', 'complete'
  message: string;
}

export default Vue.extend({
  name: "PaymentProfileSyncStatus",
  props: {
    vendor: {
      type: Object as () => Vendor,
      required: true,
    },
    autoStart: {
      type: Boolean,
      default: true, // auto-start sync if true
    },
  },
  data() {
    return {
      isVisible: true,
      eventSource: null as EventSource | null,
      messages: [] as SyncMessage[],
      progress: 0,
      hasStarted: false,
      showDetails: false, // Whether to show info/warn logs
    };
  },
  computed: {
    /**
     * Only show "info" and "warn" messages if `showDetails` is true,
     * but always show 'error', 'start', 'progress', 'complete'.
     */
    filteredMessages(): SyncMessage[] {
      return this.messages.filter((msg) => {
        if (msg.type === "error" || msg.type === "start" || msg.type === "progress" || msg.type === "complete") {
          return true;
        }
        if ((msg.type === "info" || msg.type === "warn") && this.showDetails) {
          return true;
        }
        return false;
      });
    },
  },
  methods: {
    /**
     * Starts the SSE sync process by creating an EventSource to the server.
     */
    startSync() {
      this.hasStarted = true;
      this.initiateSync();
    },

    /**
     * Creates the EventSource and sets up event listeners for SSE.
     */
    initiateSync() {
      // 1) Construct the SSE endpoint
      const baseURL =
        this.$axios && this.$axios.defaults.baseURL
          ? this.$axios.defaults.baseURL
          : "";
      const url = `${baseURL}/api/payment/sync/all`;

      // 2) Create the SSE connection
      this.eventSource = new EventSource(url, {
        withCredentials: true, // in case you need cookies/auth
      });

      // 3) Listen for custom server-sent events
      // => 'start'
      this.eventSource.addEventListener("start", (event: Event) => {
        const e = event as MessageEvent;
        this.messages.push({ type: "start", message: e.data });
      });

      // => 'progress'
      this.eventSource.addEventListener("progress", (event: Event) => {
        const e = event as MessageEvent;
        this.messages.push({ type: "progress", message: e.data });

        // Attempt to parse progress from something like "(current/total)"
        const match = e.data.match(/\((\d+)\/(\d+)\)/);
        if (match) {
          const current = parseInt(match[1], 10);
          const total = parseInt(match[2], 10);
          this.progress = (current / total) * 100;
        }
      });

      // => 'info'
      this.eventSource.addEventListener("info", (event: Event) => {
        const e = event as MessageEvent;
        this.messages.push({ type: "info", message: e.data });
      });

      // => 'warn'
      this.eventSource.addEventListener("warn", (event: Event) => {
        const e = event as MessageEvent;
        this.messages.push({ type: "warn", message: e.data });
      });

      // => 'error' (server-sent error event)
      this.eventSource.addEventListener("error", (event: Event) => {
        const e = event as MessageEvent;
        this.messages.push({ type: "error", message: e.data });
      });

      // => 'complete'
      this.eventSource.addEventListener("complete", (event: Event) => {
        const e = event as MessageEvent;
        const prevProgress = this.progress;
        this.progress = 100;
        this.messages.push({ type: "complete", message: e.data });
        if (prevProgress === 0) {
          this.messages.push({
            type: "info",
            message: "No records were processed.",
          });
        }
        // Stop listening after "complete"
        this.closeEventSource();
      });

      // 4) onmessage: unnamed messages
      this.eventSource.onmessage = (event: MessageEvent) => {
        // Some servers send data without an event name
        // If your server does that, handle it here.
        // For now, we'll just log or push it as 'info'.
        console.log("[SSE Unnamed]", event.data);
      };

      // 5) SSE general error handler
      this.eventSource.onerror = () => {
        // SSE typically auto-reconnects unless closed,
        // but we may want to close on the first error.
        this.messages.push({
          type: "error",
          message: "Connection error or server error. Stopping sync.",
        });
        this.closeEventSource();
      };
    },

    /**
     * Closes the EventSource if it's open.
     */
    closeEventSource() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    },

    /**
     * Closes the dialog and stops SSE if still active.
     */
    close() {
      this.isVisible = false;
      this.closeEventSource();
      this.$emit("close");
    },
  },
  mounted() {
    if (this.autoStart) {
      this.startSync();
    }
  },
  beforeDestroy() {
    // Cleanup SSE if the component is destroyed
    this.closeEventSource();
  },
});
